import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import BaseStore, { IBaseStore } from './BaseStore';
import { useState, useEffect } from 'react';

import { Constants } from './Constants';
import { WidgetData } from './WidgetData';
import { Dispatcher as BaseDispatcher } from './BaseDispatcher';
import * as appTheme from './appTheme';
import * as Utilities from './Utilities';
import GlobalState from '../../../_common/platform/ts/globalStates/GlobalState';

import { IValuesCollection } from '../ts/appTheme';
import { themeVars } from '../../../pdc-platform/src/ts/themes/themeConstants';

import { IBaseWidgetParameters, IWidget, IWidgetOptions } from '../../../pdc-platform/src/ts/Interfaces';

import { Action, I, IPayload, check } from '../../../pdc-platform/src/ts/actions/AppActions';

import { ICultureValues, applyCulture, getCultureValues } from '../../../_common/platform/ts/Globalization';

import { IDDAttribute, createFilters, ClickToFilterMode } from '../tsx/filtering/DrillDownModule';

import {
    applyModeIsOn,
    ApplyModes,
    getApplyMode,
    mergeAttributeFilterCollections,
    useClickToFilterFunctionality
} from '../tsx/filtering/FilterUtilities';

import {
    IGlobalAttributeFilter,
    IGlobalAttributeFilterCollection,
    IScopeOptions
} from '../../../_common/platform/tsx/filtering/Interfaces';

const Hogan = require('hogan.js');

type DrillThroughMode = 'openInNewTab' | 'openInExistingTab';

export interface IBaseWidgetStore extends IBaseStore {
    getWorkspaceID(): number;
    getTabID(): number;
    getRunTimeWidgetID(): number;
    getStaticWidgetID(): number;
    getCultureValues(): ICultureValues;
    setCultureValues(culture?: string): JQueryPromise<ICultureValues>;
    getTheme(): appTheme.IValuesCollection;
    isInDrillDown(): boolean;
    getScopeOptions(): IScopeOptions;
}

export interface IWidgetStoreStateOptions extends IWidgetOptions {}

interface ICommonWidgetStoreState {
    data?: IWidget;
    state?: any;
    isHidden?: boolean;
    refreshChanged?: number;
    cultureValues?: ICultureValues;
}

export interface IWidgetStoreStateWithOptions<TOptions> extends ICommonWidgetStoreState {
    data: IWidget;
    options: TOptions;
}

export interface IWidgetStoreState extends ICommonWidgetStoreState {
    options?: IWidgetStoreStateOptions;
    globalState?: GlobalState;
}

export interface IWidgetPrintableStoreState extends IWidgetStoreState {
    pageSize?: string;
    pageOrientation?: string;
    documentMode?: string;
}

export abstract class BaseWidgetStore<
        StateType extends IWidgetStoreState,
        ActionsType,
        DispatcherType extends BaseDispatcher,
        Glob extends IGlob
    >
    extends BaseStore<StateType, ActionsType, DispatcherType, Glob>
    implements IBaseWidgetStore
{
    constructor(
        glob: Glob,
        dispatcher: DispatcherType,
        data: IWidget,
        options: IWidgetStoreStateOptions,
        state?,
        name?: string,
        globalState?: GlobalState
    ) {
        super(glob, dispatcher, null, name);

        this._state = <StateType>{};

        this._state.data = WidgetData.normalizeData(data, glob);
        this._state.state = state;
        this._state.options = options;
        this._state.refreshChanged = 1;
        this._state.isHidden = options.initialHidden;

        if (globalState != null) {
            this._state.globalState = globalState;
        }

        const constr: any = this.constructor;

        if (typeof constr.getInitialState === 'function') {
            this._state = $.extend(true, this._state, constr.getInitialState());
        }
    }

    protected override cleanStateUp() {
        super.cleanStateUp();

        this._state = <StateType>{
            data: this._state.data,
            options: this._state.options,
            refreshChanged: 1,
            isHidden: this._state.isHidden,
            globalState: this._state.globalState
        };
    }

    static get(glob: IGlob, dispatcher, data, options, state?, ...rest): BaseWidgetStore<any, any, any, any> {
        throw new Error("Can't create instance of abstract class BaseWidgetStore");
    }

    public override get storeType(): string {
        return this.name || this._state?.data?.type;
    }

    public getWidgetParameters<TResult extends IBaseWidgetParameters<any>>(): TResult {
        if (this._state.data == null) {
            return undefined;
        }

        const widgetType = this._state.data.type;

        return this._state.data.values[widgetType] as TResult;
    }

    public useWidgetParametersHook<TResult extends IBaseWidgetParameters<any>>() {
        const [widgetParameters, setWidgetParameters] = useState(this.getWidgetParameters<TResult>());

        useEffect(() => {
            const handleOnStoreChange = () => {
                setWidgetParameters(this.getWidgetParameters<TResult>());
            };

            this.addChangeListener(handleOnStoreChange);

            return () => {
                this.removeChangeListener(handleOnStoreChange);
            };
        }, []);

        return widgetParameters;
    }

    public getTheme(): appTheme.IValuesCollection {
        return this._state.data != null && this._state.data.type != null
            ? appTheme.getWidgetTheme(this._state.data.type, this._state)
            : null;
    }

    public getCultureValues() {
        return this._state.cultureValues;
    }

    public getStaticWidgetID() {
        return this._state.data?.id;
    }

    public getRunTimeWidgetID() {
        return this._state.options?.runTimeWidgetID;
    }

    public getWidgetCID() {
        return this._state.data?.cid;
    }

    public getOptions() {
        return this._state.options;
    }

    public getWorkspaceID() {
        return this._state.options?.workspace;
    }

    public getTabID() {
        return this._state.options?.tab;
    }

    public getScopeOptions() {
        return this._state.options?.scopeOptions;
    }

    public getInlineMode() {
        return this._state.options?.inlineMode;
    }

    public getGlobalState() {
        return this._state.globalState;
    }

    public initPrintable(getStoredValues: () => any) {
        const theme = this.getTheme();
        const documentMode = theme['pdc-widget-html-document-mode'] || 'Article';
        const stateOwn = getStoredValues();

        let pageSize = theme['pdc-widget-html-page-size'] || 'A4';
        let pageOrientation = theme['pdc-widget-html-page-orientation'] || 'portrait';

        if (stateOwn != null) {
            if (stateOwn.pageSize != null) {
                pageSize = stateOwn.pageSize;
            }
            if (stateOwn.pageOrientation != null) {
                pageOrientation = stateOwn.pageOrientation;
            }
        }

        (this._state as IWidgetPrintableStoreState).pageSize = pageSize;
        (this._state as IWidgetPrintableStoreState).pageOrientation = pageOrientation;
        (this._state as IWidgetPrintableStoreState).documentMode = documentMode;
    }

    public runAction<TType extends string, TData>(type: TType, data?: TData, widgetID?: number) {
        this.glob.AppActions.abstractAction(
            this.getWorkspaceID(),
            this.getTabID(),
            widgetID != null ? widgetID : this.getStaticWidgetID(),
            {
                type: type,
                data: data
            }
        );
    }

    public updateWorkspace(targetWidgetID: number, sourceWidgetID: number) {
        this.glob.AppActions.workspaceUpdateInitiate(
            false,
            this.getWorkspaceID(),
            this.getTabID(),
            targetWidgetID,
            sourceWidgetID
        );
    }

    public isInDrillDown() {
        return this._state.options.scopeOptions != null && this._state.options.scopeOptions.drillDownWidgetID != null;
    }

    public widgetSave(id, data: any) {
        if (this._state.data == null || this._state.data.id !== id) {
            return false;
        }

        this._state.data = WidgetData.normalizeData(data, this.glob);

        return true;
    }

    public isWorkspaceHidden() {
        const wsInstance = this.glob.WorkspaceStore.getInstanceState(this._state.options.workspace);

        if (wsInstance != null && wsInstance.hiddenInTabs != null) {
            const { tab } = this._state.options;

            if (wsInstance.hiddenInTabs.indexOf(tab) >= 0) {
                return true;
            }
        }

        return false;
    }

    public isHidden() {
        return this._state.isHidden || this.isWorkspaceHidden();
    }

    public widgetSetState(workspace: number, tab: number, namespace: string, state) {
        if (this._state.options?.tab !== tab || this._state.options?.namespace !== namespace) {
            return false;
        }

        if (this.isHidden()) {
            return false;
        }

        this._state.state = state;
        return true;
    }

    public updateOptions(workspace: number, widget: number, options: any) {
        if (this._state.options.workspace !== workspace || this._state.data == null || this._state.data.id !== widget) {
            return false;
        }

        this._state.options = _.extend({}, this._state.options, options);

        return true;
    }

    public changeRefreshStatusBase(isPlus: boolean) {
        if (isPlus) {
            if (this._state.refreshChanged < 0) {
                this._state.refreshChanged = 1;
            }

            this._state.refreshChanged += 1;
        } else {
            if (this._state.refreshChanged > 0) {
                this._state.refreshChanged = -1;
            }

            this._state.refreshChanged -= 1;

            this.cleanDataCache();
        }
    }

    public getReferencedDataSources(): number[] {
        const dataSources = [];

        Utilities.JsonTraverse.traverse(this._state.data, (obj) => {
            if (obj != null && obj.hasOwnProperty('dataSourceID')) {
                dataSources.push(obj['dataSourceID']);
            }
        });

        return _.uniq(dataSources);
    }

    public updateInitiate(
        auto: boolean,
        workspace: number,
        tab: number,
        targetWidgetID: number,
        sourceWidgetID?: number
    ): boolean {
        if (this._state.options?.tab !== tab && this._state.options?.mainTab !== tab) {
            return false;
        }

        const widgetID = this.getStaticWidgetID();

        // Refresh if this Widget is the target one.
        if (targetWidgetID != null && widgetID !== targetWidgetID) {
            return false;
        }

        // Refresh if this widget is not the source of the action.
        if (widgetID === sourceWidgetID) {
            return false;
        }

        if (this.glob?.EntityModelsActions == null) {
            return false;
        }

        if (this.isHidden()) {
            return false;
        }

        const dataSources = this.getReferencedDataSources();

        if (dataSources?.length > 0) {
            this.glob.EntityModelsActions.loadModels(dataSources);
        }

        return true;
    }

    public hiddenStateChanged(hidden: boolean, widget: number, tab: number, workspace: number) {
        const widgetID = this.getStaticWidgetID() ?? this._state.data?.cid;

        if (widgetID !== widget || this._state.options?.tab !== tab) {
            return false;
        }

        if (this._state.isHidden === hidden) {
            return false;
        }

        if (!hidden) {
            if (this._state.options?.parentContainerIsShown?.() === false) {
                return false;
            }
        }

        this._state.isHidden = hidden;
        return true;
    }

    private doClickToFilter(
        storeID: number,
        dataSourceID: number,
        attributes: IDDAttribute[],
        mode: ClickToFilterMode,
        useAlways: boolean
    ): void {
        if (storeID !== this.storeID) {
            return;
        }

        if (attributes == null || attributes.length === 0) {
            return;
        }

        const theme = this.getTheme();
        const globalState = this._state.globalState;
        const isInDrillDown = this.isInDrillDown();
        const scopeOptions = this.getScopeOptions();

        if (mode == null) {
            mode = theme[themeVars.clickToFilterMode];
        }

        if (!isInDrillDown && !useAlways && mode !== 'drillThrough') {
            if (
                !globalState.filterWidgetExists('AttributeFilter') &&
                !globalState.filterWidgetExists('ToolbarFilter')
            ) {
                return;
            }

            const clickToFilterIsUsed = useClickToFilterFunctionality(theme);

            if (!clickToFilterIsUsed) {
                return;
            }
        }

        const cultureValues = this.getCultureValues();
        const isDrillThrough = mode === 'drillThrough';
        const filters = createFilters(
            attributes,
            dataSourceID,
            isDrillThrough,
            globalState,
            scopeOptions,
            this.glob.EntityModelsStore,
            isDrillThrough,
            theme,
            cultureValues,
            this.getRunTimeWidgetID(),
            this.glob
        );
        const { attributeFilters, periodFilter } = filters;

        if (attributeFilters == null && periodFilter == null) {
            return;
        }

        if (isDrillThrough) {
            const drillThroughWorkspaceID: number = theme[themeVars.drillThroughWorkspaceID];

            if (drillThroughWorkspaceID == null) {
                this.glob.AppActions.showNotification_Warning('Workspace for Drill Through is not specified.');
                return;
            }

            const tabTitleTemplate = theme[themeVars.drillThroughTabTitleTemplate];

            let tabTitle: string;

            if (typeof tabTitleTemplate === 'string' && tabTitleTemplate.trim() !== '') {
                const inputData = {};

                let variables = globalState.getAllVariables(
                    dataSourceID,
                    cultureValues,
                    scopeOptions,
                    undefined,
                    undefined,
                    isDrillThrough
                );

                this.makeInputDataForTabTitleTemplate(inputData, variables);

                variables = globalState.getAllVariables(
                    dataSourceID,
                    cultureValues,
                    scopeOptions,
                    filters.attributeFilters
                );
                this.makeInputDataForTabTitleTemplate(inputData, variables);

                let hoganOptions;

                if (tabTitleTemplate.indexOf('<%') >= 0 && tabTitleTemplate.indexOf('%>') > 2) {
                    hoganOptions = { delimiters: '<% %>' };
                }

                tabTitle = Hogan.compile(tabTitleTemplate, hoganOptions).render(inputData);
            } else {
                tabTitle = 'Drill Through';
            }

            const paramString = globalState.getFilterAsURLParameters(scopeOptions, filters, tabTitle);
            const drillThroughMode: DrillThroughMode = theme[themeVars.drillThroughMode];

            if (drillThroughMode === 'openInExistingTab') {
                this.glob.mainRouter.navigate(
                    'workspaces/activate/' + drillThroughWorkspaceID + '/?' + encodeURI(paramString),
                    {
                        trigger: true
                    }
                );
            } else {
                this.glob.mainRouter.navigate(
                    '/tabs/1000/workspaces/' + drillThroughWorkspaceID + '/?' + encodeURI(paramString),
                    {
                        trigger: true
                    }
                );
            }

            return;
        }

        if (attributeFilters == null) {
            return;
        }

        if (isInDrillDown) {
            this.glob.AppActions.widgetDoDrillDown(
                this.getTabID(),
                scopeOptions.drillDownWidgetID,
                scopeOptions.drillDownlevel,
                dataSourceID,
                attributeFilters
            );
        } else {
            if (mode === 'delayedApply') {
                if (this.clickToFilterInterval != null) {
                    clearInterval(this.clickToFilterInterval);
                }

                this.clickToFilterInterval = setInterval(() => {
                    clearInterval(this.clickToFilterInterval);
                    this.clickToFilterInterval = null;

                    this.applyClickToFilters(attributeFilters, globalState, theme);
                }, 2000);
            } else if (mode === 'cumulative') {
                if (this.clickToFilterInterval != null) {
                    clearInterval(this.clickToFilterInterval);
                }

                if (this.clickToFilters == null) {
                    this.clickToFilters = attributeFilters;
                } else {
                    _.each(attributeFilters, (newFilters, dataSourceID) => {
                        let existingFilters = this.clickToFilters[dataSourceID];

                        if (existingFilters == null) {
                            this.clickToFilters[dataSourceID] = existingFilters = {};
                        }

                        _.each(newFilters, (newFilter) => {
                            const values = newFilter.values;

                            if (values != null && values.length > 0) {
                                const existingFilter = existingFilters[newFilter.id];

                                if (existingFilter != null) {
                                    if (existingFilter.values == null) {
                                        existingFilter.values = values;
                                    } else {
                                        values.forEach((value) => {
                                            if (
                                                existingFilter.values.find((existingValue) => {
                                                    return (
                                                        existingValue.value === value.value &&
                                                        existingValue.displayValue === value.displayValue
                                                    );
                                                }) == null
                                            ) {
                                                existingFilter.values.push(value);
                                            }
                                        });
                                    }
                                } else {
                                    existingFilters[newFilter.id] = { ...newFilter };
                                }
                            }
                        });
                    });
                }

                this.clickToFilterInterval = setInterval(() => {
                    clearInterval(this.clickToFilterInterval);
                    this.clickToFilterInterval = null;

                    this.applyClickToFilters(this.clickToFilters, globalState, theme);

                    this.clickToFilters = null;
                }, 2000);
            } else {
                this.applyClickToFilters(attributeFilters, globalState, theme);
            }
        }
    }

    private makeInputDataForTabTitleTemplate(inputData: { [key: string]: any }, variables: IGlobalAttributeFilter[]) {
        variables.forEach((variable) => {
            if (Array.isArray(variable.values) && variable.values.length > 0) {
                const value = variable.values[0];

                if (value != null && value.value != null) {
                    let variableName = variable.attribute;

                    if (inputData[variableName] == null) {
                        inputData[variableName] = value.value;
                    }

                    if (variableName[0] === '@') {
                        if (variableName.length > 1) {
                            variableName = variableName.slice(1);

                            if (inputData[variableName] == null) {
                                inputData[variableName] = value.value;
                            }
                        }
                    } else {
                        variableName = '@' + variableName;

                        if (inputData[variableName] == null) {
                            inputData[variableName] = value.value;
                        }
                    }
                }
            }
        });
    }

    private clickToFilterInterval: NodeJS.Timer;
    private clickToFilters: IGlobalAttributeFilterCollection;

    private applyClickToFilters(
        filtersToApply: IGlobalAttributeFilterCollection,
        globalState: GlobalState,
        theme: IValuesCollection
    ) {
        let newCollection = globalState.getNotAppliedAttributeFilterCollection(true);

        if (newCollection != null) {
            mergeAttributeFilterCollections(newCollection, filtersToApply, null);
        } else {
            newCollection = filtersToApply;
        }

        globalState.putAttributeFiltersOnShelf(newCollection, false);

        if (globalState.applyModeIsOn(theme)) {
            globalState.triggerFiltersAreOnShelfEvent();
        } else {
            globalState.applyShelvedFilters(theme);
        }
    }

    protected processAbstractAction(workspace: number, tab: number, widget: number, payload: I.IAbstractActionPayload) {
        // WidgetID should not be checked here because Drill Down widget does not check it in its processAbstractAction().
        if (payload == null) {
            return false;
        }

        if (this._state.options?.workspace !== workspace) {
            return false;
        }

        if (this._state.options?.tab !== tab) {
            return false;
        }

        return true;
    }

    public setCultureValues(culture?: string, theme?: IValuesCollection): JQueryPromise<ICultureValues> {
        const result = $.Deferred<ICultureValues>();

        if (theme == null) {
            theme = this.getTheme();
        }

        const cultureValues = getCultureValues(theme, culture);

        if (this._state.cultureValues == null || this._state.cultureValues.culture !== cultureValues.culture) {
            applyCulture(cultureValues.culture, () => {
                this._state.cultureValues = { ...cultureValues };
                result.resolve(this._state.cultureValues);
            });
        } else {
            this._state.cultureValues = { ...cultureValues };
            result.resolve(this._state.cultureValues);
        }

        return result.promise();
    }

    public allowApplyData(theme?: IValuesCollection): boolean {
        if (this._state != null && this._state.globalState != null) {
            if (theme == null) {
                theme = this.getTheme();
            }

            if (!applyModeIsOn(this._state.globalState, theme)) {
                return true;
            }

            const applyMode = getApplyMode(theme);

            if (applyMode === ApplyModes.MANUAL) {
                if (!this._state.isHidden) {
                    if (this._state.globalState.applyButtonWasPressed) {
                        return true;
                    } else {
                        // Drilling down do apply automatically for all widgets, but the top one.
                        const drillDownLevel = this._state.options.scopeOptions?.drillDownlevel;

                        if (drillDownLevel > 0) {
                            return true;
                        } else {
                            // Issue 76570. If not Filter Widget exists, Apply is auto.
                            return !this._state.globalState.filterWidgetExists();
                        }
                    }
                } else {
                    return false;
                }
            }
        }

        return true;
    }

    protected onWorkspaceApplyChanges(tab: number, namespace: string, changesType: I.ChangesType) {
        if (this._state.options.tab !== tab || this._state.options.namespace !== namespace) {
            return false;
        }

        if (changesType === 'filters' && !this._state.isHidden) {
            this._state.globalState.applyButtonWasPressed = true;
        }

        return true;
    }

    public applyWorkspaceChanges() {
        this.glob.AppActions.workspaceApplyChanges(this._state.options.tab, this._state.options.namespace, 'data');
    }

    private dropApplyButtonWasPressed() {
        if (this._state != null && this._state.globalState != null) {
            this._state.globalState.applyButtonWasPressed = false;
        }
    }

    protected onWidgetRemove() {
        this.dropApplyButtonWasPressed();
        return true;
    }

    protected override doNotProcessEvent(actionType: string | number): boolean {
        return (
            actionType === Constants.TOOLBAR_BUTTONS_CANCEL_ON ||
            actionType === Constants.TOOLBAR_BUTTONS_CANCEL_OFF ||
            actionType === Constants.LOADING_PANEL_SHOW ||
            actionType === Constants.LOADING_PANEL_HIDE
        );
    }

    // private logEvent(action: Action<Constants>) {
    //     let storeName: string;

    //     if (this._state.data != null) {
    //         storeName = this._state.data.type;
    //     }

    //     console.log(`Store: ${storeName ?? this.storeID}, Action: ${action.actionType}`);
    // }

    protected override eventsHandler(payload: IPayload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const action = payload.action;

        //this.logEvent(action);

        if (check<I.WIDGET_SAVE>(action, Constants.WIDGET_SAVE)) {
            this.setCultureValues().done(() => {
                this.dropApplyButtonWasPressed();
                this.emitChange(this.widgetSave(action.id, action.data));
            });
        } else if (check<I.WIDGET_UPDATE_OPTIONS>(action, Constants.WIDGET_UPDATE_OPTIONS)) {
            this.setCultureValues().done(() => {
                this.dropApplyButtonWasPressed();
                this.emitChange(this.updateOptions(action.workspace, action.widget, action.options));
            });
        } else if (check<I.WORKSPACE_SET_STATE>(action, Constants.WORKSPACE_SET_STATE)) {
            this.setCultureValues().done(() => {
                this.emitChange(this.widgetSetState(action.id, action.tab, action.namespace, action.state));
            });
        } else if (check<I.WORKSPACE_UPDATE_INITIATE>(action, Constants.WORKSPACE_UPDATE_INITIATE)) {
            if (
                this.updateInitiate(
                    action.auto,
                    action.workspace,
                    action.tab,
                    action.targetWidgetID,
                    action.sourceWidgetID
                )
            ) {
                this.emitChange();
            }
        } else if (check<I.WIDGET_SET_HIDDEN>(action, Constants.WIDGET_SET_HIDDEN)) {
            if (this.hiddenStateChanged(action.hidden, action.widget, action.tab, action.workspace)) {
                this.triggerChange();
            }
        } else if (check<I.WIDGET_DO_CLICK_TO_FILTER>(action, Constants.WIDGET_DO_CLICK_TO_FILTER)) {
            this.doClickToFilter(action.storeID, action.dataSourceID, action.attributes, action.mode, action.useAlways);
        } else if (check<I.WIDGET_REMOVE>(action, Constants.WIDGET_REMOVE)) {
            if (this.onWidgetRemove()) {
                this.emitChange();
            }
        } else if (check<I.WORKSPACE_APPLY_CHANGES>(action, Constants.WORKSPACE_APPLY_CHANGES)) {
            this.onWorkspaceApplyChanges(action.tab, action.namespace, action.changesType);
        } else if (check<I.ABSTRACT_ACTION>(action, Constants.ABSTRACT_ACTION)) {
            this.emitChange(this.processAbstractAction(action.workspace, action.tab, action.widget, action.payload));
        }

        return true;
    }
}
